import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { IntakeFormData } from '../lib/types/domains/intake-form';
import { initialIntakeFormData } from '../lib/mockData/mockIntakeFormData';

interface IntakeFormState {
  // Form data
  formData: IntakeFormData;
  
  // UI state
  currentStep: number;
  errors: Record<string, string>;
  isSubmitting: boolean;
  
  // Auto-save state
  lastSavedAt: Date | null;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  
  // Actions
  updateField: (path: string, value: any) => void;
  setSection: (data: Partial<IntakeFormData>) => void;
  setStep: (step: number) => void;
  setErrors: (errors: Record<string, string>) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  
  // Auto-save actions
  setLastSavedAt: (date: Date) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  
  // Utility actions
  resetForm: () => void;
  loadFormData: (data: Partial<IntakeFormData>) => void;
  getFieldValue: (path: string) => any;
}

// Helper function to update nested fields
const updateNestedField = (state: IntakeFormData, path: string, value: any): void => {
  const keys = path.split('.');
  let current: any = state;
  
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }
  
  current[keys[keys.length - 1]] = value;
};

// Helper function to get nested field value
const getNestedFieldValue = (data: IntakeFormData, path: string): any => {
  const keys = path.split('.');
  let current: any = data;
  
  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[key];
  }
  
  return current;
};

// Ensure complete form data structure
const ensureCompleteFormData = (data: any): IntakeFormData => {
  return {
    personalInfo: data?.personalInfo || {},
    practitionerQuestions: data?.practitionerQuestions || {},
    healthConcerns: data?.healthConcerns || {},
    preExistingConditions: data?.preExistingConditions || {},
    familyMedicalHistory: data?.familyMedicalHistory || {},
    lifestyleProfile: data?.lifestyleProfile || {},
    sleepProfile: data?.sleepProfile || {},
    dentalProfile: data?.dentalProfile || {},
    toxinScreen: data?.toxinScreen || {},
    physicalActivity: data?.physicalActivity || {},
    psychosocialProfile: data?.psychosocialProfile || {},
    dietaryProfile: data?.dietaryProfile || {},
    supplementsMedications: data?.supplementsMedications || {},
    symptomAssessment: data?.symptomAssessment || {},
    healthAndMedicalConditions: data?.healthAndMedicalConditions || {},
    clientStatement: data?.clientStatement || {}
  };
};

export const useIntakeFormStore = create<IntakeFormState>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        formData: ensureCompleteFormData(initialIntakeFormData),
        currentStep: 0,
        errors: {},
        isSubmitting: false,
        lastSavedAt: null,
        hasUnsavedChanges: false,
        isSaving: false,
        
        // Actions
        updateField: (path: string, value: any) => {
          set((state) => {
            updateNestedField(state.formData, path, value);
            // Clear field error when value changes
            if (state.errors[path]) {
              delete state.errors[path];
            }
            state.hasUnsavedChanges = true;
          });
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[IntakeFormStore] Field updated:', path, value);
          }
        },
        
        setSection: (data: Partial<IntakeFormData>) => {
          set((state) => {
            Object.assign(state.formData, data);
            state.hasUnsavedChanges = true;
          });
        },
        
        setStep: (step: number) => {
          set((state) => {
            state.currentStep = step;
          });
        },
        
        setErrors: (errors: Record<string, string>) => {
          set((state) => {
            state.errors = errors;
          });
        },
        
        setSubmitting: (isSubmitting: boolean) => {
          set((state) => {
            state.isSubmitting = isSubmitting;
          });
        },
        
        setLastSavedAt: (date: Date) => {
          set((state) => {
            state.lastSavedAt = date;
          });
        },
        
        setHasUnsavedChanges: (hasChanges: boolean) => {
          set((state) => {
            state.hasUnsavedChanges = hasChanges;
          });
        },
        
        setIsSaving: (isSaving: boolean) => {
          set((state) => {
            state.isSaving = isSaving;
          });
        },
        
        resetForm: () => {
          set((state) => {
            state.formData = ensureCompleteFormData(initialIntakeFormData);
            state.currentStep = 0;
            state.errors = {};
            state.isSubmitting = false;
            state.lastSavedAt = null;
            state.hasUnsavedChanges = false;
            state.isSaving = false;
          });
        },
        
        loadFormData: (data: Partial<IntakeFormData>) => {
          set((state) => {
            state.formData = ensureCompleteFormData({ ...state.formData, ...data });
            state.hasUnsavedChanges = false;
          });
        },
        
        getFieldValue: (path: string) => {
          return getNestedFieldValue(get().formData, path);
        }
      })),
      {
        name: 'intake-form-storage',
        partialize: (state) => ({
          formData: state.formData,
          currentStep: state.currentStep
        })
      }
    ),
    {
      name: 'IntakeFormStore'
    }
  )
);

// Selectors for common use cases
export const selectFormData = (state: IntakeFormState) => state.formData;
export const selectCurrentStep = (state: IntakeFormState) => state.currentStep;
export const selectErrors = (state: IntakeFormState) => state.errors;
export const selectIsSubmitting = (state: IntakeFormState) => state.isSubmitting;
export const selectSaveStatus = (state: IntakeFormState) => ({
  lastSavedAt: state.lastSavedAt,
  hasUnsavedChanges: state.hasUnsavedChanges,
  isSaving: state.isSaving
});